import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { PageShell } from '../components/layout/PageShell';
import { FeedComposer } from '../components/feed/FeedComposer';
import { FeedSearch } from '../components/feed/FeedSearch';
import { FeedEmptyState } from '../components/feed/FeedEmptyState';
import { FeedPostCard } from '../components/feed/FeedPostCard';
import { FeedThread } from '../components/feed/FeedThread';
import { liveFeedHandleAtom } from '../stores/sdk/live-feed-atom';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

export function LiveFeedView() {
  const { postId } = useParams<{ postId?: string }>();
  const liveFeed = useAtomValue(liveFeedHandleAtom);
  const [, setRevision] = useState(0);
  const [threadError, setThreadError] = useState<string | null>(null);
  const [homeError, setHomeError] = useState<string | null>(null);

  useEffect(() => {
    if (!liveFeed) return;
    return liveFeed.onChange(() => {
      setRevision((n) => n + 1);
    });
  }, [liveFeed]);

  useEffect(() => {
    if (!liveFeed) return;
    void liveFeed.markSeen();
  }, [liveFeed]);

  useEffect(() => {
    if (!liveFeed || !postId) return;
    let cancelled = false;
    void liveFeed
      .loadThread(postId)
      .then(() => {
        if (!cancelled) setThreadError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setThreadError(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [liveFeed, postId]);

  useEffect(() => {
    if (!liveFeed) return;
    const scroller = document.querySelector('[data-testid="page-shell-scroll"]');
    if (!(scroller instanceof HTMLElement)) return;
    let loading = false;
    const onScroll = () => {
      if (loading) return;
      const remaining = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      if (remaining > 80) return;
      loading = true;
      void liveFeed
        .loadOlder()
        .then(() => {
          setHomeError(null);
        })
        .catch((err: unknown) => {
          setHomeError(err instanceof Error ? err.message : String(err));
        })
        .finally(() => {
          loading = false;
        });
    };
    scroller.addEventListener('scroll', onScroll);
    return () => {
      scroller.removeEventListener('scroll', onScroll);
    };
  }, [liveFeed]);

  const items = liveFeed?.homeItems ?? [];
  const thread = liveFeed?.openThread;

  return (
    <PageShell
      title="Live Feed"
      description="Short updates from people and personas in this account."
    >
      <div data-testid="live-feed-view" className="mx-auto flex max-w-2xl flex-col gap-6">
        <div data-testid="feed-search-slot">
          <FeedSearch />
        </div>
        {homeError ? (
          <p className="text-sm text-destructive" role="alert">
            {homeError}
          </p>
        ) : null}
        {liveFeed ? <FeedComposer liveFeed={liveFeed} /> : null}
        {postId ? (
          <section data-testid="feed-permalink" className="space-y-2">
            {threadError ? (
              <p className="text-sm text-destructive" role="alert">
                {threadError}
              </p>
            ) : thread && liveFeed ? (
              <FeedThread thread={thread} liveFeed={liveFeed} />
            ) : (
              <LoadingState label="Loading post…" />
            )}
          </section>
        ) : null}
        {!postId && items.length === 0 && liveFeed ? <FeedEmptyState /> : null}
        {items.length > 0 ? (
          <ol data-testid="feed-home-list" className="space-y-3">
            {items.map((post) => (
              <li key={post.id}>
                <FeedPostCard post={post} liveFeed={liveFeed ?? undefined} />
              </li>
            ))}
          </ol>
        ) : null}
      </div>
    </PageShell>
  );
}
